import { ArrowLeft, Save } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { toast } from 'sonner'
import { useUserName, useUserRol, useTienePermiso} from '../App'
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService'
import { RechazoModal } from "./RechazoModal"
import { Button } from "./ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"

interface NMXA90731FormData {
  fecha: string
  hora: string
  gradosC: string
  porcentajeHR: string
  
  // Datos de las 5 muestras
  gramos1: string
  resultado1: string
  gramos2: string
  resultado2: string
  gramos3: string
  resultado3: string
  gramos4: string
  resultado4: string
  gramos5: string
  resultado5: string
  
  promedio: string
  
  observaciones: string
  mostrarObservaciones: boolean
  realizo: string
  superviso: string
  numeroSolicitud?: string
  idTermohigrometro?: string
  desv_metodo?: string;
  coef_variacion?: string;
}

interface NMXA90731FormProps {
  onBack: () => void
  pedidoId: string
  pedidoRowId?: string
  mode: 'create' | 'view' | 'edit'
  initialData?: Partial<NMXA90731FormData>
  realizoUser?: string
  onSave?: (data: NMXA90731FormData) => void
  onRefreshBackend?: () => void
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

export function NMXA90731Form({ onBack, pedidoId, pedidoRowId, mode, initialData, realizoUser, onSave, onRefreshBackend, onAutosave }: NMXA90731FormProps) {
  const globalRol = useUserRol();
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const userName = useUserName();
  const effectiveRol = globalRol;

  const [formData, setFormData] = useState<NMXA90731FormData>({
    fecha: "",
    hora: "",
    gradosC: "",
    porcentajeHR: "",
    
    gramos1: "",
    resultado1: "",
    gramos2: "",
    resultado2: "",
    gramos3: "",
    resultado3: "",
    gramos4: "",
    resultado4: "",
    gramos5: "",
    resultado5: "",
    
    promedio: "",
    
    observaciones: "",
    realizo: (mode === 'create' && realizoUser) ? realizoUser : initialData?.realizo || "",
    superviso: "",
    desv_metodo: initialData?.desv_metodo || "",
    coef_variacion: initialData?.coef_variacion || "",
    ...initialData,
    mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true,
  })

  const [editMode, setEditMode] = useState(false);
  const [estatus, setEstatus] = useState<string>(initialData && (initialData as any).ACEPTADO ? (initialData as any).ACEPTADO : '');
  const [rechazoModalOpen, setRechazoModalOpen] = useState(false);
  const [rechazoLoading, setRechazoLoading] = useState(false);

  useEffect(() => {
    // Preservar la lógica correcta para el campo realizo
    const realizoValue = (mode === 'create' && realizoUser) ? realizoUser : initialData?.realizo || "";

    setFormData(prev => ({
      ...prev,
      ...initialData,
      realizo: realizoValue,
      mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true,
    }))
    setEstatus((initialData as any)?.ACEPTADO || '')
    setEditMode(false)
  }, [initialData, mode, realizoUser])

  useEffect(() => {
    if (mode === 'view' && effectiveRol !== 'ADMIN') {
      setEditMode(false)
    }
  }, [effectiveRol, mode])



  const FACTOR_CORRECCION = 0.05;

  const handleInputChange = (field: keyof NMXA90731FormData, value: any) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      // Calcular resultado automáticamente al cambiar gramos
      if (["gramos1", "gramos2", "gramos3", "gramos4", "gramos5"].includes(field)) {
        const idx = field.replace("gramos", "");
        const gramos = parseFloat(value);
        if (!isNaN(gramos)) {
          const resultado = (gramos / FACTOR_CORRECCION).toString();
          const match = resultado.match(/^-?\d+(?:\.\d{0,2})?/);
          (newData as any)[`resultado${idx}`] = match ? match[0] : resultado;
        } else {
          (newData as any)[`resultado${idx}`] = "";
        }
      }
      return newData;
    });
  }

  // Calcular promedio automáticamente
  function truncateTo2(num: number) {
    return Math.trunc(num * 100) / 100;
  }

  useEffect(() => {
    const resultados = [
      formData.resultado1,
      formData.resultado2,
      formData.resultado3,
      formData.resultado4,
      formData.resultado5
    ].filter(r => r && !isNaN(parseFloat(r))).map(r => parseFloat(r))

    if (resultados.length > 0) {
      const promedio = resultados.reduce((sum, val) => sum + val, 0) / resultados.length
      const promedioTrunc = truncateTo2(promedio).toFixed(2);
      setFormData(prev => ({ ...prev, promedio: promedioTrunc }))
    } else {
      setFormData(prev => ({ ...prev, promedio: "" }))
    }
  }, [formData.resultado1, formData.resultado2, formData.resultado3, formData.resultado4, formData.resultado5])

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      if (onSave) {
        onSave(formData)
      }
    } finally {
      setGuardando(false)
    }
  }

  // --- ADMIN ACTIONS ---
  // Siempre toma ROW_ID y ACEPTADO de initialData (robusto)
  const [autosaveRowId, setAutosaveRowId] = useState<string>((initialData as any)?.ROW_ID || (initialData as any)?.row_id || '');
  const [autosaveNomId, setAutosaveNomId] = useState<string>((initialData as any)?.nomId || '');
  const rowId = autosaveRowId || (formData as any)?.ROW_ID || '';
  const nomId = autosaveNomId;
  const pedidoValue = (formData as any).PEDIDO || pedidoId || '';
  const pruebaValue = (formData as any).PRUEBA || (initialData as any)?.PRUEBA || 'NMX-A-9073/1-INNTEX-2018 MASA DE NO TEJIDOS';
  // Lógica robusta para saber si la prueba está aceptada o rechazada
  const aceptadoRaw = (initialData as any)?.ACEPTADO ?? (initialData as any)?.aceptado;
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : '';
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado';

  const formPayload = () => formData;

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'NMX-A-9073/1-INNTEX-2018 MASA DE NO TEJIDOS',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onBack()
  }

  const handleAccion = async (accion: 'aceptado' | 'rechazado', razonRechazo?: string) => {
    const confirmMsg = accion === 'aceptado'
      ? 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba NMX-A-9073-1?'
      : 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba NMX-A-9073-1?';
    if (accion === 'rechazado' && !razonRechazo) {
      setRechazoModalOpen(true);
      return;
    }
    if (!window.confirm(confirmMsg)) return;
    const payload = {
      ...formData,
      ROW_ID: rowId,
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      ACEPTAR: accion === 'aceptado' ? 'aceptado' : 'rechazado',
      ACEPTADO: accion,
      ...(accion === 'rechazado' ? { RAZON_RECHAZO: razonRechazo } : {})
    };
    try {
      if (accion === 'rechazado') setRechazoLoading(true);
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, accion, accion === 'rechazado' ? (payload as any).RAZON_RECHAZO : undefined, (payload as any).superviso, nomId);
      setEstatus(payload.ACEPTAR);
      setEditMode(false);
      setRechazoModalOpen(false);
      setRechazoLoading(false);
      toast.success(`Prueba ${accion === 'aceptado' ? 'aceptada' : 'rechazada'} correctamente.`);
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err: any) {
      setRechazoLoading(false);
      alert('Error al enviar al backend: ' + (err.message || err));
    }
  };

  const handleEditar = () => setEditMode(true);

  const handleGuardarEdicion = async () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de NMX-A-9073-1? Esta acción no se puede deshacer.')) return;
    const payload = {
      ...formData,
      ROW_ID: rowId,
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      ACEPTAR: 'aceptado',
      ACEPTADO: 'aceptado'
    };
    try {
      onSave({ ...payload, _EDIT_MODE: true } as any);
    } catch (err) {
      console.error('Error al guardar edición:', err);
    }
  };

  // Misma lógica que AnalisisQuimicoForm: solo readOnly en modo 'view'
  const isReadOnly = mode === 'view' && !editMode

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={handleBackFlush}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">
                NMX-A-9073/1-INNTEX-2018
              </h1>
              <p className="text-muted-foreground">
                DETERMINACIÓN DE LA MASA POR UNIDAD DE ÁREA
              </p>
              <p className="text-sm text-primary font-medium mt-1">Método: MET-7.2-9</p>
            </div>
          </div>
          <div className="flex gap-2">
            {/* Admin action buttons */}
            {mode === 'view' && !editMode && !isAceptado && puedeRevisar && (
              <>
                <Button variant="default" onClick={() => handleAccion('aceptado')}>Aceptar</Button>
                <Button variant="destructive" onClick={() => setRechazoModalOpen(true)}>Rechazar</Button>
                <Button variant="secondary" onClick={handleEditar}>Editar</Button>
              </>
            )}
            {mode === 'view' && editMode && !isAceptado && puedeRevisar && (
              <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
            )}
            {/* Botón guardar en modo create */}
            {mode === 'create' && (
              <Button onClick={handleSave} disabled={guardando}>
                <Save className="w-4 h-4 mr-2" />
                {guardando ? 'Guardando...' : 'Guardar'}
              </Button>
            )}
          </div>
        </div>

        {/* Información del Pedido */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-primary">Información del Pedido</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Pedido No: {pedidoId}</p>
          </CardContent>
        </Card>

        {/* Datos de la Solicitud */}
        <Card>
          <CardHeader>
            <CardTitle>Datos de la Solicitud</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div>
                <Label htmlFor="numeroSolicitud">Número de solicitud</Label>
                <Input
                  id="numeroSolicitud"
                  type="text"
                  placeholder="Ejemplo: 12345"
                  value={formData.numeroSolicitud || ''}
                  onChange={e => handleInputChange('numeroSolicitud', e.target.value)}
                  readOnly={isReadOnly}
                  className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}
                />
              </div>
              <div>
                <Label htmlFor="idTermohigrometro">ID de termohigrómetro</Label>
                <select
                  id="idTermohigrometro"
                  value={formData.idTermohigrometro || ''}
                  onChange={e => handleInputChange('idTermohigrometro', e.target.value)}
                  disabled={isReadOnly}
                  className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}
                >
                  <option value="">Selecciona...</option>
                  <option value="E-59">E-59</option>
                  <option value="E-57">E-57</option>
                  <option value="E-56">E-56</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Parámetros de Prueba */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-primary">Parámetros de Prueba</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border border-border">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left font-medium">FECHA:</th>
                    <th className="border border-border p-3 text-left font-medium">HORA:</th>
                    <th className="border border-border p-3 text-left font-medium">°C:</th>
                    <th className="border border-border p-3 text-left font-medium">% HR:</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-2">
                      <Input
                        type="date"
                        value={formData.fecha}
                        onChange={(e) => handleInputChange('fecha', e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border border-border p-2">
                      <Input
                        type="time"
                        value={formData.hora}
                        onChange={(e) => { if (e.target.value) handleInputChange('hora', e.target.value) }}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border border-border p-2">
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="°C"
                        value={formData.gradosC}
                        onChange={(e) => handleInputChange('gradosC', e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border border-border p-2">
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="%"
                        value={formData.porcentajeHR}
                        onChange={(e) => handleInputChange('porcentajeHR', e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2" colSpan={2}>
                      <Label htmlFor="desv_metodo">Desviación al método</Label>
                      <Input
                        id="desv_metodo"
                        type="text"
                        placeholder="Describa desviación al método"
                        value={formData.desv_metodo || ""}
                        onChange={e => handleInputChange('desv_metodo', e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border border-border p-2" colSpan={2}>
                      <Label htmlFor="coef_variacion">Coeficiente de variación</Label>
                      <Input
                        id="coef_variacion"
                        type="text"
                        placeholder="Ej. 0.05"
                        value={formData.coef_variacion || ""}
                        onChange={e => handleInputChange('coef_variacion', e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Tabla Principal y Firmas */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tabla de Datos */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-primary">Datos de Muestras</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border border-border">
                    <thead>
                      <tr className="bg-muted">
                        <th className="border border-border p-3 text-center font-medium">NO.</th>
                        <th className="border border-border p-3 text-center font-medium">GRAMOS</th>
                        <th className="border border-border p-3 text-center font-medium">RESULTADO</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[1, 2, 3, 4, 5].map((num) => (
                        <tr key={num}>
                          <td className="border border-border p-2 text-center font-medium bg-muted">
                            {num}
                          </td>
                          <td className="border border-border p-1">
                            <Input
                              type="number"
                              step="0.0001"
                              placeholder="Gramos"
                              value={formData[`gramos${num}` as keyof NMXA90731FormData] as string}
                              onChange={(e) => handleInputChange(`gramos${num}` as keyof NMXA90731FormData, e.target.value)}
                              disabled={isReadOnly}
                              className="text-center border-0 bg-transparent"
                            />
                          </td>
                          <td className="border border-border p-1">
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Resultado"
                              value={formData[`resultado${num}` as keyof NMXA90731FormData] as string}
                              onChange={(e) => handleInputChange(`resultado${num}` as keyof NMXA90731FormData, e.target.value)}
                              disabled={isReadOnly}
                              className="text-center border-0 bg-transparent"
                            />
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-accent">
                        <td className="border border-border p-2 text-center font-medium">
                          PROMEDIO
                        </td>
                        <td className="border border-border p-2 text-center">
                          {/* Celda vacía */}
                        </td>
                        <td className="border border-border p-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <span className="font-medium">
                              {formData.promedio || "0.00"}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              g/m²
                            </span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Firmas */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-primary">Firmas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="realizo">REALIZÓ:</Label>
                  <Input
                    id="realizo"
                    type="text"
                    placeholder="Nombre de quien realizó"
                    value={formData.realizo}
                    readOnly
                    className="mt-2 bg-muted-foreground/10 cursor-not-allowed"
                  />
                </div>
                <div>
                  <Label htmlFor="superviso">SUPERVISÓ:</Label>
                  <Input
                    id="superviso"
                    type="text"
                    placeholder="Nombre de quien supervisó"
                    value={formData.superviso}
                    readOnly
                    className="mt-2 bg-muted-foreground/10 cursor-not-allowed"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Observaciones */}
        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-primary">Observaciones</CardTitle>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Mostrar en Reporte Cliente
              <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                onCheckedChange={(checked) => handleInputChange('mostrarObservaciones', checked)} />
            </span>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="observaciones">OBSERVACIONES:</Label>
              <Textarea
                id="observaciones"
                placeholder="Ingrese las observaciones aquí..."
                value={formData.observaciones}
                onChange={(e) => handleInputChange('observaciones', e.target.value)}
                disabled={isReadOnly}
                className="mt-2 min-h-[100px]"
              />
            </div>
          </CardContent>
        </Card>

        {/* Modal de rechazo */}
        <RechazoModal
          open={rechazoModalOpen}
          onClose={() => setRechazoModalOpen(false)}
          loading={rechazoLoading}
          onSubmit={(razon) => handleAccion('rechazado', razon)}
        />
      </div>
    </div>
  )
}